import React, { useState, useEffect } from 'react';
import vaccinationService from '../../../services/vaccinationService';
import animalService from '../../../services/animalService';
import medicineService from '../../../services/medicineService';

const PAGE_SIZE_SCHEDULE  = 10;  // animals per page
const PAGE_SIZE_ALERTS    = 5;
const PAGE_SIZE_MEDICINES = 12;

// ── Pagination Control ────────────────────────────────────────────────────────
const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, pageSize }) => {
  if (totalPages <= 1) return null;
  const start = (currentPage - 1) * pageSize + 1;
  const end   = Math.min(currentPage * pageSize, totalItems);
  return (
    <div style={paginationStyles.wrapper}>
      <span style={paginationStyles.info}>Showing {start}–{end} of {totalItems}</span>
      <div style={paginationStyles.controls}>
        <button onClick={() => onPageChange(1)}               disabled={currentPage === 1}          style={{ ...paginationStyles.btn, ...(currentPage === 1          ? paginationStyles.btnDisabled : {}) }}>«</button>
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}          style={{ ...paginationStyles.btn, ...(currentPage === 1          ? paginationStyles.btnDisabled : {}) }}>‹ Prev</button>
        <span style={paginationStyles.pageIndicator}>Page {currentPage} of {totalPages}</span>
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} style={{ ...paginationStyles.btn, ...(currentPage === totalPages ? paginationStyles.btnDisabled : {}) }}>Next ›</button>
        <button onClick={() => onPageChange(totalPages)}      disabled={currentPage === totalPages} style={{ ...paginationStyles.btn, ...(currentPage === totalPages ? paginationStyles.btnDisabled : {}) }}>»</button>
      </div>
    </div>
  );
};

// ── Group vaccinations by animal ──────────────────────────────────────────────
const groupByAnimal = (vaccinations) => {
  const map = {};
  vaccinations.forEach((v) => {
    if (!map[v.animal_id]) {
      map[v.animal_id] = {
        animal_id:   v.animal_id,
        animal_name: v.animal_name,
        tag_number:  v.tag_number,
        breed:       v.breed,
        active:      [],
        completed:   [],
      };
    }
    if (v.status === 'completed') {
      map[v.animal_id].completed.push(v);
    } else {
      map[v.animal_id].active.push(v);
    }
  });
  return Object.values(map);
};

// ── History Modal ─────────────────────────────────────────────────────────────
const HistoryModal = ({ animal, onClose }) => {
  if (!animal) return null;
  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={{ ...styles.modal, maxWidth: '700px' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 style={{ ...styles.modalTitle, marginBottom: '4px' }}>🕐 Vaccination History</h2>
            <p style={{ color: '#7f8c8d', fontSize: '14px', margin: 0 }}>
              {animal.animal_name} &nbsp;—&nbsp; <span style={styles.tag}>{animal.tag_number}</span>
            </p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        {animal.completed.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#95a5a6' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📋</div>
            <p>No completed vaccinations yet for this animal.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.headerRow}>
                  <th style={styles.th}>#</th>
                  <th style={styles.th}>Vaccination Type</th>
                  <th style={styles.th}>Last Date</th>
                  <th style={styles.th}>Completed On</th>
                  <th style={styles.th}>Interval</th>
                  <th style={styles.th}>Veterinarian</th>
                  <th style={styles.th}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {animal.completed.map((v, i) => (
                  <tr key={v.id} style={styles.row}>
                    <td style={{ ...styles.td, color: '#95a5a6', fontSize: '13px' }}>{i + 1}</td>
                    <td style={styles.td}>{v.vaccination_type}</td>
                    <td style={styles.td}>{formatDate(v.last_date)}</td>
                    <td style={styles.td}>{formatDate(v.due_date)}</td>
                    <td style={styles.td}><span style={styles.intervalBadge}>🔄 {v.next_interval_days}d</span></td>
                    <td style={styles.td}>{v.veterinarian || 'N/A'}</td>
                    <td style={styles.td}>{v.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: '20px', textAlign: 'right' }}>
          <button onClick={onClose} style={{ ...styles.cancelButton, flex: 'unset', padding: '10px 24px' }}>Close</button>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const VaccinationsTab = () => {
  const [overdueVaccinations, setOverdueVaccinations] = useState([]);
  const [dueSoonVaccinations, setDueSoonVaccinations] = useState([]);
  const [allVaccinations,     setAllVaccinations]     = useState([]);
  const [animals,             setAnimals]             = useState([]);
  const [medicines,           setMedicines]           = useState([]);
  const [loading,             setLoading]             = useState(true);
  const [error,               setError]               = useState(null);
  const [activeView,          setActiveView]          = useState('schedule');
  const [submitting,          setSubmitting]          = useState(false);

  // Pagination
  const [schedulePage, setSchedulePage] = useState(1);
  const [overduePage,  setOverduePage]  = useState(1);
  const [dueSoonPage,  setDueSoonPage]  = useState(1);
  const [medicinePage, setMedicinePage] = useState(1);

  // History modal
  const [historyAnimal, setHistoryAnimal] = useState(null);

  // Modals
  const [showAddScheduleModal, setShowAddScheduleModal] = useState(false);
  const [showAddModal,         setShowAddModal]         = useState(false);
  const [showDetailModal,      setShowDetailModal]      = useState(false);
  const [showEditModal,        setShowEditModal]        = useState(false);
  const [selectedMedicine,     setSelectedMedicine]     = useState(null);

  const [formData, setFormData] = useState({
    medicine_name: '', disease_purpose: '', ingredients: '', how_to_use: '',
  });
  const [scheduleForm, setScheduleForm] = useState({
    animal_id: '', vaccination_type: '',
    last_date: new Date().toISOString().split('T')[0],
    interval_days: '180', veterinarian: '', notes: '',
  });

  const intervalOptions = [
    { label: 'Every 30 days (Monthly)',   value: '30'  },
    { label: 'Every 60 days (2 Months)',  value: '60'  },
    { label: 'Every 90 days (3 Months)',  value: '90'  },
    { label: 'Every 180 days (6 Months)', value: '180' },
    { label: 'Every 365 days (1 Year)',   value: '365' },
  ];

  useEffect(() => {
    fetchVaccinations();
    fetchAnimals();
    fetchMedicines();
  }, []);

  const fetchAnimals = async () => {
    try { const r = await animalService.getAllAnimals(); setAnimals(r.data || []); }
    catch (err) { console.error('Error fetching animals:', err); }
  };
  const fetchMedicines = async () => {
    try { const r = await medicineService.getAllMedicines(); setMedicines(r.data || []); }
    catch (err) { console.error('Error fetching medicines:', err); }
  };
  const fetchVaccinations = async () => {
    try {
      setLoading(true); setError(null);
      const [overdue, dueSoon, all] = await Promise.all([
        vaccinationService.getOverdue(),
        vaccinationService.getDueSoon(),
        vaccinationService.getAllVaccinations(),
      ]);
      setOverdueVaccinations(overdue.data || []);
      setDueSoonVaccinations(dueSoon.data  || []);
      setAllVaccinations(all.data          || []);
      setSchedulePage(1); setOverduePage(1); setDueSoonPage(1); setMedicinePage(1);
    } catch (err) {
      console.error('Error fetching vaccinations:', err);
      setError('Failed to load vaccinations. Please try again.');
    } finally { setLoading(false); }
  };

  const calculateDueDate = (lastDate, intervalDays) => {
    const d = new Date(lastDate);
    d.setDate(d.getDate() + parseInt(intervalDays));
    return d.toISOString().split('T')[0];
  };

  const handleAddSchedule = async (e) => {
    e.preventDefault();
    if (!scheduleForm.animal_id || !scheduleForm.vaccination_type || !scheduleForm.last_date) {
      alert('Please fill all required fields.'); return;
    }
    setSubmitting(true);
    try {
      const dueDate = calculateDueDate(scheduleForm.last_date, scheduleForm.interval_days);
      const payload = {
        vaccination_type:   scheduleForm.vaccination_type,
        last_date:          scheduleForm.last_date,
        due_date:           dueDate,
        next_interval_days: parseInt(scheduleForm.interval_days),
        veterinarian:       scheduleForm.veterinarian,
        notes:              scheduleForm.notes,
        status:             'scheduled',
      };
      if (scheduleForm.animal_id === 'all') {
        await Promise.all(animals.map(a => vaccinationService.createVaccination({ ...payload, animal_id: a.id })));
        alert(`✅ Vaccination schedule added for all ${animals.length} animals!`);
      } else {
        await vaccinationService.createVaccination({ ...payload, animal_id: scheduleForm.animal_id });
        alert('✅ Vaccination schedule added successfully!');
      }
      setShowAddScheduleModal(false);
      setScheduleForm({ animal_id: '', vaccination_type: '', last_date: new Date().toISOString().split('T')[0], interval_days: '180', veterinarian: '', notes: '' });
      fetchVaccinations();
    } catch { alert('❌ Failed to add schedule. Please try again.'); }
    finally { setSubmitting(false); }
  };

  const handleMarkAsComplete = async (vaccinationId) => {
    if (!window.confirm('Mark this vaccination as complete? This will auto-schedule the next one.')) return;
    try {
      await vaccinationService.completeVaccination(vaccinationId);
      alert('✅ Marked as complete! Next schedule created automatically.');
      fetchVaccinations();
    } catch { alert('❌ Failed to mark vaccination as complete.'); }
  };

  const handleDeleteVaccination = async (vaccinationId) => {
    if (!window.confirm('Delete this vaccination record?')) return;
    try {
      await vaccinationService.deleteVaccination(vaccinationId);
      alert('✅ Vaccination record deleted.');
      fetchVaccinations();
    } catch { alert('❌ Failed to delete vaccination.'); }
  };

  const handleAddMedicine = async (e) => {
    e.preventDefault();
    try {
      await medicineService.createMedicine(formData);
      alert('✅ Medicine recipe saved!');
      setShowAddModal(false);
      setFormData({ medicine_name: '', disease_purpose: '', ingredients: '', how_to_use: '' });
      fetchMedicines();
    } catch { alert('❌ Failed to save medicine.'); }
  };

  const handleEditMedicine = async (e) => {
    e.preventDefault();
    try {
      await medicineService.updateMedicine(selectedMedicine.id, formData);
      alert('✅ Medicine updated successfully!');
      setShowEditModal(false); setSelectedMedicine(null);
      setFormData({ medicine_name: '', disease_purpose: '', ingredients: '', how_to_use: '' });
      fetchMedicines();
    } catch { alert('❌ Failed to update medicine.'); }
  };

  const handleDeleteMedicine = async (id) => {
    if (!window.confirm('Delete this medicine?')) return;
    try {
      await medicineService.deleteMedicine(id);
      alert('✅ Medicine deleted!');
      setShowDetailModal(false); setSelectedMedicine(null);
      fetchMedicines();
    } catch { alert('❌ Failed to delete medicine.'); }
  };

  const openDetailModal = (m) => { setSelectedMedicine(m); setShowDetailModal(true); };
  const openEditModal   = (m) => {
    setSelectedMedicine(m);
    setFormData({ medicine_name: m.medicine_name, disease_purpose: m.disease_purpose, ingredients: m.ingredients, how_to_use: m.how_to_use });
    setShowDetailModal(false); setShowEditModal(true);
  };

  // ── Derived data ──────────────────────────────────────────────────────────
  const groupedAnimals   = groupByAnimal(allVaccinations);
  const paginatedAnimals = groupedAnimals.slice((schedulePage - 1) * PAGE_SIZE_SCHEDULE, schedulePage * PAGE_SIZE_SCHEDULE);
  const paginatedOverdue  = overdueVaccinations.slice((overduePage  - 1) * PAGE_SIZE_ALERTS,    overduePage  * PAGE_SIZE_ALERTS);
  const paginatedDueSoon  = dueSoonVaccinations.slice((dueSoonPage  - 1) * PAGE_SIZE_ALERTS,    dueSoonPage  * PAGE_SIZE_ALERTS);
  const paginatedMedicines = medicines.slice(          (medicinePage - 1) * PAGE_SIZE_MEDICINES, medicinePage * PAGE_SIZE_MEDICINES);

  // ── Loading / Error ───────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
      <h2 style={{ color: '#666' }}>Loading vaccinations...</h2>
    </div>
  );
  if (error) return (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
      <h2 style={{ color: '#e74c3c', marginBottom: '10px' }}>Error</h2>
      <p style={{ color: '#666' }}>{error}</p>
      <button onClick={fetchVaccinations} style={styles.retryButton}>🔄 Retry</button>
    </div>
  );

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#2c3e50', marginBottom: '8px' }}>Vaccination Management</h2>
          <p style={{ color: '#7f8c8d', fontSize: '14px' }}>Monitor and manage livestock vaccination schedules</p>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button onClick={() => setShowAddScheduleModal(true)} style={styles.scheduleButton}>💉 Add Schedule</button>
          <button onClick={() => setShowAddModal(true)}         style={styles.addButton}>➕ Add Medicine Recipe</button>
        </div>
      </div>

      {/* ── View Toggle ── */}
      <div style={{ marginBottom: '24px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        {[
          { key: 'schedule',  label: `📅 Full Schedule (${groupedAnimals.length} animals)` },
          { key: 'alerts',    label: `🚨 Alerts (${overdueVaccinations.length + dueSoonVaccinations.length})` },
          { key: 'medicines', label: `💊 Medicine Details (${medicines.length})` },
        ].map(({ key, label }) => (
          <button key={key} onClick={() => setActiveView(key)}
            style={{ ...styles.viewButton, background: activeView === key ? '#3498db' : '#ecf0f1', color: activeView === key ? 'white' : '#2c3e50' }}>
            {label}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          FULL SCHEDULE — grouped by animal
      ══════════════════════════════════════════════════════════════════════ */}
      {activeView === 'schedule' && (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.headerRow}>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Animal</th>
                <th style={styles.th}>Tag</th>
                <th style={styles.th}>Active Vaccinations</th>
                <th style={styles.th}>History</th>
              </tr>
            </thead>
            <tbody>
              {groupedAnimals.length === 0 ? (
                <tr>
                  <td colSpan="5" style={styles.noData}>
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>💉</div>
                    No vaccinations scheduled yet.<br />
                    <span style={{ fontSize: '14px', color: '#3498db', cursor: 'pointer' }}
                      onClick={() => setShowAddScheduleModal(true)}>
                      + Click here to add first schedule
                    </span>
                  </td>
                </tr>
              ) : (
                paginatedAnimals.map((ag, idx) => (
                  <tr key={ag.animal_id} style={{ ...styles.row, verticalAlign: 'top' }}>

                    {/* # */}
                    <td style={{ ...styles.td, color: '#95a5a6', fontSize: '13px', paddingTop: '20px', width: '40px' }}>
                      {(schedulePage - 1) * PAGE_SIZE_SCHEDULE + idx + 1}
                    </td>

                    {/* Animal name */}
                    <td style={{ ...styles.td, paddingTop: '20px', fontWeight: '700', minWidth: '110px' }}>
                      {ag.animal_name}
                      <div style={{ fontSize: '12px', color: '#7f8c8d', fontWeight: '400', marginTop: '2px' }}>{ag.breed}</div>
                    </td>

                    {/* Tag */}
                    <td style={{ ...styles.td, paddingTop: '20px', width: '90px' }}>
                      <span style={styles.tag}>{ag.tag_number}</span>
                    </td>

                    {/* Stacked active vaccinations */}
                    <td style={{ ...styles.td, padding: '12px 16px' }}>
                      {ag.active.length === 0 ? (
                        <span style={{ color: '#95a5a6', fontSize: '13px', fontStyle: 'italic' }}>
                          No active vaccinations
                        </span>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {ag.active.map((vacc) => (
                            <div key={vacc.id} style={styles.vaccRow}>
                              {/* Vacc name + badges */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                                <span style={{ fontWeight: '600', fontSize: '14px', color: '#2c3e50' }}>
                                  {vacc.vaccination_type}
                                </span>
                                <span style={getStatusStyle(vacc.status)}>{vacc.status}</span>
                                <span style={styles.intervalBadge}>🔄 {vacc.next_interval_days}d</span>
                              </div>
                              {/* Dates + vet */}
                              <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: '#7f8c8d', flexWrap: 'wrap', marginBottom: '6px' }}>
                                <span>Last: <strong>{formatDate(vacc.last_date)}</strong></span>
                                <span>Due: <strong style={{ color: vacc.status === 'overdue' ? '#e74c3c' : '#2c3e50' }}>{formatDate(vacc.due_date)}</strong></span>
                                {vacc.veterinarian && <span>🩺 {vacc.veterinarian}</span>}
                              </div>
                              {/* Per-vacc action buttons */}
                              <div style={{ display: 'flex', gap: '6px' }}>
                                {vacc.status !== 'completed' && (
                                  <button onClick={() => handleMarkAsComplete(vacc.id)} style={styles.smallCompleteButton}>✓ Done</button>
                                )}
                                <button onClick={() => handleDeleteVaccination(vacc.id)} style={styles.smallDeleteButton}>🗑️ Delete</button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>

                    {/* History button (animal-level) */}
                    <td style={{ ...styles.td, paddingTop: '20px', width: '110px' }}>
                      <button onClick={() => setHistoryAnimal(ag)} style={styles.historyButton}>
                        🕐 History
                        {ag.completed.length > 0 && (
                          <span style={styles.historyBadge}>{ag.completed.length}</span>
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <Pagination
            currentPage={schedulePage}
            totalPages={Math.ceil(groupedAnimals.length / PAGE_SIZE_SCHEDULE)}
            onPageChange={setSchedulePage}
            totalItems={groupedAnimals.length}
            pageSize={PAGE_SIZE_SCHEDULE}
          />
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          ALERTS — per vaccination (unchanged)
      ══════════════════════════════════════════════════════════════════════ */}
      {activeView === 'alerts' && (
        <div>
          {overdueVaccinations.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <div style={styles.alertHeader('#e74c3c')}>
                <span style={{ fontSize: '20px' }}>🔴</span>
                <span style={{ fontWeight: '700', fontSize: '16px' }}>OVERDUE VACCINATIONS ({overdueVaccinations.length})</span>
              </div>
              <div style={styles.alertsContainer}>
                {paginatedOverdue.map((vacc) => (
                  <div key={vacc.id} style={styles.alertCard('#ffebee')}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '700', fontSize: '16px', marginBottom: '4px' }}>{vacc.animal_name} ({vacc.tag_number})</div>
                      <div style={{ color: '#666', fontSize: '14px', marginBottom: '8px' }}>{vacc.vaccination_type} — {vacc.breed}</div>
                      <div style={{ fontSize: '13px', color: '#e74c3c', fontWeight: '600' }}>Due: {formatDate(vacc.due_date)} (OVERDUE)</div>
                    </div>
                    <button onClick={() => handleMarkAsComplete(vacc.id)} style={styles.completeButton}>✓ Mark Done</button>
                  </div>
                ))}
              </div>
              <Pagination currentPage={overduePage} totalPages={Math.ceil(overdueVaccinations.length / PAGE_SIZE_ALERTS)} onPageChange={setOverduePage} totalItems={overdueVaccinations.length} pageSize={PAGE_SIZE_ALERTS} />
            </div>
          )}

          {dueSoonVaccinations.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <div style={styles.alertHeader('#f39c12')}>
                <span style={{ fontSize: '20px' }}>🟡</span>
                <span style={{ fontWeight: '700', fontSize: '16px' }}>DUE SOON ({dueSoonVaccinations.length})</span>
              </div>
              <div style={styles.alertsContainer}>
                {paginatedDueSoon.map((vacc) => (
                  <div key={vacc.id} style={styles.alertCard('#fffbea')}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '700', fontSize: '16px', marginBottom: '4px' }}>{vacc.animal_name} ({vacc.tag_number})</div>
                      <div style={{ color: '#666', fontSize: '14px', marginBottom: '8px' }}>{vacc.vaccination_type} — {vacc.breed}</div>
                      <div style={{ fontSize: '13px', color: '#f39c12', fontWeight: '600' }}>Due: {formatDate(vacc.due_date)}</div>
                    </div>
                    <button onClick={() => handleMarkAsComplete(vacc.id)} style={styles.completeButton}>✓ Mark Done</button>
                  </div>
                ))}
              </div>
              <Pagination currentPage={dueSoonPage} totalPages={Math.ceil(dueSoonVaccinations.length / PAGE_SIZE_ALERTS)} onPageChange={setDueSoonPage} totalItems={dueSoonVaccinations.length} pageSize={PAGE_SIZE_ALERTS} />
            </div>
          )}

          {overdueVaccinations.length === 0 && dueSoonVaccinations.length === 0 && (
            <div style={styles.noAlerts}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>✅</div>
              <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px', color: '#2ecc71' }}>All Clear!</h3>
              <p style={{ color: '#7f8c8d' }}>No overdue or upcoming vaccinations</p>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          MEDICINES
      ══════════════════════════════════════════════════════════════════════ */}
      {activeView === 'medicines' && (
        <div>
          {medicines.length === 0 ? (
            <div style={styles.noAlerts}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>💊</div>
              <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px', color: '#7f8c8d' }}>No Medicines Added Yet</h3>
              <p style={{ color: '#7f8c8d' }}>Click "Add Medicine Recipe" to add your first medicine</p>
            </div>
          ) : (
            <>
              <div style={styles.medicineGrid}>
                {paginatedMedicines.map((medicine) => (
                  <div key={medicine.id} style={styles.medicineCard} onClick={() => openDetailModal(medicine)}>
                    <div style={styles.medicineIcon}>💊</div>
                    <div style={styles.medicineName}>{medicine.medicine_name}</div>
                    <div style={styles.medicineDisease}>{medicine.disease_purpose || 'No disease specified'}</div>
                    <div style={styles.medicineFooter}>Click to view details →</div>
                  </div>
                ))}
              </div>
              <Pagination currentPage={medicinePage} totalPages={Math.ceil(medicines.length / PAGE_SIZE_MEDICINES)} onPageChange={setMedicinePage} totalItems={medicines.length} pageSize={PAGE_SIZE_MEDICINES} />
            </>
          )}
        </div>
      )}

      {/* ── History Modal ─────────────────────────────────────────────────── */}
      <HistoryModal animal={historyAnimal} onClose={() => setHistoryAnimal(null)} />

      {/* ── Add Schedule Modal ────────────────────────────────────────────── */}
      {showAddScheduleModal && (
        <div style={styles.modalOverlay} onClick={() => setShowAddScheduleModal(false)}>
          <div style={{ ...styles.modal, maxWidth: '640px' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={styles.modalTitle}>💉 Add Vaccination Schedule</h2>
              <button onClick={() => setShowAddScheduleModal(false)} style={styles.closeButton}>✕</button>
            </div>
            <form onSubmit={handleAddSchedule}>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Animal <span style={{ color: '#e74c3c' }}>*</span></label>
                  <select value={scheduleForm.animal_id} onChange={(e) => setScheduleForm({ ...scheduleForm, animal_id: e.target.value })} style={styles.input} required>
                    <option value="">Select Animal</option>
                    <option value="all">🐄 All Animals</option>
                    {animals.map((a) => <option key={a.id} value={a.id}>{a.tag_number} — {a.name || a.type} ({a.breed})</option>)}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Vaccination / Medicine <span style={{ color: '#e74c3c' }}>*</span></label>
                  <select value={scheduleForm.vaccination_type} onChange={(e) => setScheduleForm({ ...scheduleForm, vaccination_type: e.target.value })} style={styles.input} required>
                    <option value="">Select Medicine</option>
                    {medicines.length > 0
                      ? medicines.map((m) => <option key={m.id} value={m.medicine_name}>{m.medicine_name}{m.disease_purpose ? ` — ${m.disease_purpose}` : ''}</option>)
                      : <option disabled>No medicines added yet</option>}
                  </select>
                </div>
              </div>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Last Vaccination Date <span style={{ color: '#e74c3c' }}>*</span></label>
                  <input type="date" value={scheduleForm.last_date} onChange={(e) => setScheduleForm({ ...scheduleForm, last_date: e.target.value })} style={styles.input} required />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Repeat Interval <span style={{ color: '#e74c3c' }}>*</span></label>
                  <select value={scheduleForm.interval_days} onChange={(e) => setScheduleForm({ ...scheduleForm, interval_days: e.target.value })} style={styles.input}>
                    {intervalOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
              </div>
              {scheduleForm.last_date && scheduleForm.interval_days && (
                <div style={styles.dueDatePreview}>
                  📅 Next vaccination due: <strong>{formatDate(calculateDueDate(scheduleForm.last_date, scheduleForm.interval_days))}</strong>
                </div>
              )}
              <div style={styles.formGroup}>
                <label style={styles.label}>Veterinarian Name</label>
                <input type="text" value={scheduleForm.veterinarian} onChange={(e) => setScheduleForm({ ...scheduleForm, veterinarian: e.target.value })} style={styles.input} placeholder="Enter veterinarian name (optional)" />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Notes</label>
                <textarea value={scheduleForm.notes} onChange={(e) => setScheduleForm({ ...scheduleForm, notes: e.target.value })} style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }} placeholder="Add dosage, notes..." rows="3" />
              </div>
              <div style={styles.modalButtons}>
                <button type="button" onClick={() => setShowAddScheduleModal(false)} style={styles.cancelButton}>Cancel</button>
                <button type="submit" style={styles.submitButton} disabled={submitting}>{submitting ? '⏳ Saving...' : '💉 Save Schedule'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Add Medicine Modal ────────────────────────────────────────────── */}
      {showAddModal && (
        <div style={styles.modalOverlay} onClick={() => setShowAddModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Add Medicine Recipe</h2>
            <form onSubmit={handleAddMedicine}>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Medicine Name <span style={{ color: '#e74c3c' }}>*</span></label>
                  <input type="text" value={formData.medicine_name} onChange={(e) => setFormData({ ...formData, medicine_name: e.target.value })} style={styles.input} placeholder="Enter medicine name" required />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Disease / Purpose</label>
                  <input type="text" value={formData.disease_purpose} onChange={(e) => setFormData({ ...formData, disease_purpose: e.target.value })} style={styles.input} placeholder="What disease it treats" />
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Ingredients</label>
                <textarea value={formData.ingredients} onChange={(e) => setFormData({ ...formData, ingredients: e.target.value })} style={{ ...styles.input, minHeight: '100px', resize: 'vertical' }} rows="4" placeholder="List all ingredients..." />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>How to Use</label>
                <textarea value={formData.how_to_use} onChange={(e) => setFormData({ ...formData, how_to_use: e.target.value })} style={{ ...styles.input, minHeight: '100px', resize: 'vertical' }} rows="4" placeholder="Usage instructions..." />
              </div>
              <div style={styles.modalButtons}>
                <button type="button" onClick={() => { setShowAddModal(false); setFormData({ medicine_name: '', disease_purpose: '', ingredients: '', how_to_use: '' }); }} style={styles.cancelButton}>Cancel</button>
                <button type="submit" style={styles.submitButton}>Save Medicine</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Medicine Detail Modal ─────────────────────────────────────────── */}
      {showDetailModal && selectedMedicine && (
        <div style={styles.modalOverlay} onClick={() => setShowDetailModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={styles.modalTitle}>💊 {selectedMedicine.medicine_name}</h2>
              <button onClick={() => setShowDetailModal(false)} style={styles.closeButton}>✕</button>
            </div>
            <div style={styles.detailSection}><div style={styles.detailLabel}>🦠 Disease / Purpose</div><div style={styles.detailValue}>{selectedMedicine.disease_purpose || 'Not specified'}</div></div>
            <div style={styles.detailSection}><div style={styles.detailLabel}>🧪 Ingredients</div><div style={{ ...styles.detailValue, whiteSpace: 'pre-line' }}>{selectedMedicine.ingredients || 'Not specified'}</div></div>
            <div style={styles.detailSection}><div style={styles.detailLabel}>📋 How to Use</div><div style={{ ...styles.detailValue, whiteSpace: 'pre-line' }}>{selectedMedicine.how_to_use || 'Not specified'}</div></div>
            <div style={styles.modalButtons}>
              <button onClick={() => openEditModal(selectedMedicine)} style={styles.editButton}>✏️ Edit Medicine</button>
              <button onClick={() => handleDeleteMedicine(selectedMedicine.id)} style={styles.deleteButton}>🗑️ Delete Medicine</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Medicine Modal ───────────────────────────────────────────── */}
      {showEditModal && selectedMedicine && (
        <div style={styles.modalOverlay} onClick={() => setShowEditModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Edit Medicine Recipe</h2>
            <form onSubmit={handleEditMedicine}>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Medicine Name <span style={{ color: '#e74c3c' }}>*</span></label>
                  <input type="text" value={formData.medicine_name} onChange={(e) => setFormData({ ...formData, medicine_name: e.target.value })} style={styles.input} required />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Disease / Purpose</label>
                  <input type="text" value={formData.disease_purpose} onChange={(e) => setFormData({ ...formData, disease_purpose: e.target.value })} style={styles.input} />
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Ingredients</label>
                <textarea value={formData.ingredients} onChange={(e) => setFormData({ ...formData, ingredients: e.target.value })} style={{ ...styles.input, minHeight: '100px', resize: 'vertical' }} rows="4" />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>How to Use</label>
                <textarea value={formData.how_to_use} onChange={(e) => setFormData({ ...formData, how_to_use: e.target.value })} style={{ ...styles.input, minHeight: '100px', resize: 'vertical' }} rows="4" />
              </div>
              <div style={styles.modalButtons}>
                <button type="button" onClick={() => { setShowEditModal(false); setSelectedMedicine(null); setFormData({ medicine_name: '', disease_purpose: '', ingredients: '', how_to_use: '' }); }} style={styles.cancelButton}>Cancel</button>
                <button type="submit" style={styles.submitButton}>Update Medicine</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const getStatusStyle = (status) => {
  const map = {
    overdue:   { background: '#ffebee', color: '#c62828', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' },
    due_soon:  { background: '#fff3e0', color: '#e65100', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' },
    upcoming:  { background: '#e8f5e9', color: '#2e7d32', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' },
    scheduled: { background: '#e3f2fd', color: '#1565c0', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' },
    completed: { background: '#f5f5f5', color: '#616161', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' },
  };
  return map[status] || map['scheduled'];
};

const paginationStyles = {
  wrapper:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: '1px solid #f0f0f0', background: '#fafafa', borderRadius: '0 0 12px 12px', flexWrap: 'wrap', gap: '10px' },
  info:          { fontSize: '13px', color: '#7f8c8d' },
  controls:      { display: 'flex', alignItems: 'center', gap: '6px' },
  btn:           { padding: '6px 12px', border: '1px solid #ddd', borderRadius: '6px', background: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  btnDisabled:   { opacity: 0.4, cursor: 'not-allowed' },
  pageIndicator: { padding: '6px 14px', background: '#3498db', color: 'white', borderRadius: '6px', fontSize: '13px', fontWeight: '600' },
};

const styles = {
  scheduleButton:      { padding: '10px 20px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  addButton:           { padding: '10px 20px', background: '#3498db', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  viewButton:          { padding: '12px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  dueDatePreview:      { background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: '8px', padding: '12px 16px', fontSize: '14px', color: '#2e7d32', marginBottom: '16px' },
  intervalBadge:       { background: '#e3f2fd', color: '#1565c0', padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' },
  alertHeader:         (c) => ({ background: c, color: 'white', padding: '16px 20px', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', gap: '12px', fontWeight: '700' }),
  alertsContainer:     { background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' },
  alertCard:           (bg) => ({ background: bg, padding: '20px', borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: '16px' }),
  completeButton:      { padding: '10px 20px', background: '#2ecc71', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' },
  smallCompleteButton: { padding: '5px 12px', background: '#2ecc71', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  smallDeleteButton:   { padding: '5px 12px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  historyButton:       { padding: '7px 14px', background: '#8e44ad', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '6px' },
  historyBadge:        { background: 'white', color: '#8e44ad', borderRadius: '10px', padding: '1px 6px', fontSize: '11px', fontWeight: '700' },
  noAlerts:            { background: 'white', padding: '80px 20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', textAlign: 'center' },
  tableContainer:      { background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'auto' },
  table:               { width: '100%', borderCollapse: 'collapse' },
  headerRow:           { background: '#f8f9fa' },
  th:                  { padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#2c3e50', borderBottom: '2px solid #e0e0e0' },
  row:                 { borderBottom: '1px solid #f0f0f0' },
  td:                  { padding: '16px', fontSize: '14px', color: '#2c3e50' },
  tag:                 { background: '#3498db', color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '700' },
  noData:              { padding: '60px 20px', textAlign: 'center', color: '#95a5a6', fontSize: '16px' },
  retryButton:         { marginTop: '20px', padding: '12px 24px', background: '#3498db', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  vaccRow:             { background: '#f8f9fa', borderRadius: '8px', padding: '10px 14px', borderLeft: '3px solid #3498db' },
  medicineGrid:        { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '20px', marginBottom: '16px' },
  medicineCard:        { background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', cursor: 'pointer', textAlign: 'center' },
  medicineIcon:        { fontSize: '48px', marginBottom: '12px' },
  medicineName:        { fontSize: '16px', fontWeight: '700', color: '#2c3e50', marginBottom: '8px' },
  medicineDisease:     { fontSize: '13px', color: '#7f8c8d', marginBottom: '16px' },
  medicineFooter:      { fontSize: '12px', color: '#3498db', fontWeight: '600' },
  modalOverlay:        { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modal:               { background: 'white', padding: '30px', borderRadius: '12px', width: '90%', maxWidth: '600px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)', maxHeight: '90vh', overflow: 'auto' },
  modalTitle:          { fontSize: '22px', fontWeight: '700', marginBottom: '24px', color: '#2c3e50' },
  formRow:             { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' },
  formGroup:           { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' },
  label:               { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  input:               { width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' },
  modalButtons:        { display: 'flex', gap: '12px', marginTop: '24px' },
  cancelButton:        { flex: 1, padding: '12px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  submitButton:        { flex: 1, padding: '12px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  closeButton:         { padding: '8px 12px', background: '#f0f0f0', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: '700', color: '#666' },
  detailSection:       { marginBottom: '20px', padding: '16px', background: '#f8f9fa', borderRadius: '8px' },
  detailLabel:         { fontSize: '13px', fontWeight: '700', color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' },
  detailValue:         { fontSize: '14px', color: '#2c3e50', lineHeight: '1.6' },
  editButton:          { flex: 1, padding: '12px', background: '#3498db', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  deleteButton:        { flex: 1, padding: '12px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
};

export default VaccinationsTab;